import React, { useState, useEffect } from 'react';
import { Bot, LogOut, Sun, Moon } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useAuthStore } from '../../stores/authStore';
import ChatInterface from '../../components/ChatInterface';

interface Assistant {
  id: string;
  nombre: string;
  descripcion: string;
  modelo: string;
  avatar_url: string;
}

export default function UserDashboard() {
  const { user, signOut } = useAuthStore();
  const [assistants, setAssistants] = useState<Assistant[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showChat, setShowChat] = useState(false);
  const [selectedAssistant, setSelectedAssistant] = useState<Assistant | null>(null);
  const [isDarkMode, setIsDarkMode] = useState(false);

  useEffect(() => {
    if (user) {
      fetchAssistants();
    }
  }, [user]);

  useEffect(() => {
    document.documentElement.classList.toggle('dark', isDarkMode);
  }, [isDarkMode]);

  const fetchAssistants = async () => {
    try {
      // First get user's group ID
      const { data: userData, error: userError } = await supabase
        .from('usuarios')
        .select('grupo_id')
        .eq('id', user.id)
        .single();

      if (userError) throw userError;

      // Then get the assistants_grupos entries for this group
      const { data: groupAssistants, error: groupError } = await supabase
        .from('asistentes_grupos')
        .select('asistente_id')
        .eq('grupo_id', userData.grupo_id);

      if (groupError) throw groupError;

      // Finally get the actual assistants
      const { data: assistantsData, error: assistantsError } = await supabase
        .from('asistentes')
        .select(`
          id,
          nombre,
          descripcion,
          modelo,
          avatar_url
        `)
        .eq('estado', 'activo')
        .in('id', groupAssistants.map(ga => ga.asistente_id))
        .order('nombre');

      if (assistantsError) throw assistantsError;
      setAssistants(assistantsData || []);
    } catch (error) {
      console.error('Error fetching assistants:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleStartChat = (assistant: Assistant) => {
    setSelectedAssistant(assistant);
    setShowChat(true);
  };

  const handleLogout = async () => {
    try {
      await signOut();
    } catch (error) {
      console.error('Error during logout:', error);
    }
  };

  const toggleTheme = () => {
    setIsDarkMode(!isDarkMode);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-100 dark:bg-gray-900">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${isDarkMode ? 'dark' : ''}`}>
      {/* Top Navigation */}
      <nav className="bg-white dark:bg-gray-800 shadow-md">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <Bot className="w-8 h-8 text-blue-500 dark:text-blue-400" />
              <span className="ml-2 font-semibold text-xl text-gray-900 dark:text-white">SYNEPSIA</span>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={toggleTheme}
                className="p-2 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full"
              >
                {isDarkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
              </button>
              <button
                onClick={handleLogout}
                className="flex items-center space-x-2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
              >
                <LogOut className="w-5 h-5" />
                <span>Cerrar Sesión</span>
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {assistants.map((assistant) => (
            <div
              key={assistant.id}
              className="bg-white dark:bg-gray-800 rounded-xl shadow-sm hover:shadow-md transition-shadow duration-200 overflow-hidden"
            >
              <div className="relative">
                <img
                  src={assistant.avatar_url || 'https://images.unsplash.com/photo-1464582883107-8adf2dca8a9f?w=400&auto=format&fit=crop&q=80'}
                  alt={assistant.nombre}
                  className="w-full h-48 object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
              </div>
              <div className="p-4">
                <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-1">{assistant.nombre}</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">{assistant.modelo}</p>
                <p className="text-gray-600 dark:text-gray-300 text-sm line-clamp-3">{assistant.descripcion}</p>
                <button
                  onClick={() => handleStartChat(assistant)}
                  className="mt-4 w-full flex items-center justify-center px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                >
                  <Bot className="w-5 h-5 mr-2" />
                  Iniciar Chat
                </button>
              </div>
            </div>
          ))}

          {assistants.length === 0 && (
            <div className="col-span-full text-center py-12">
              <Bot className="w-16 h-16 mx-auto text-gray-400 dark:text-gray-600 mb-4" />
              <p className="text-gray-500 dark:text-gray-400">No hay asistentes disponibles</p>
            </div>
          )}
        </div>
      </main>

      {/* Chat Interface Modal */}
      {showChat && (
        <ChatInterface 
          onClose={() => {
            setShowChat(false);
            setSelectedAssistant(null);
          }}
          selectedAssistant={selectedAssistant}
        />
      )}
    </div>
  );
}